import { useEffect, useState } from "react";
import { Popover } from "@base-ui/react/popover";
import type {
  KeyValueStore,
  KeyValueEditorType,
} from "@repo/types/ui/keyValuePairs";
import type { RecordId } from "@repo/types/ui/tables";
import KeyValueMenuView from "./KeyValueMenuView";
import { KeyValueMenuEdit } from "./KeyValueMenuEdit";

export default function KeyValueMenu({
  selectedKey,
  items,
  align,
  enumType,
  onValueChange,
  triggerRenderer,
  handleTriggerClick,
  customActionTriggerRenderer,
  customActionTemplateRenderer,
  popupClassNames,
  positionerSide,
  positionerAlign,
  itemsEditable,
  itemsDeletable,
  onDeleteClick,
  editText,
  mode = "default",
  maxHeight,
}: KeyValueStore) {
  const [currentSelectedKey, setCurrentSelectedKey] = useState<RecordId[]>(
    Array.isArray(selectedKey)
      ? selectedKey
      : selectedKey !== undefined && selectedKey !== null
        ? [selectedKey]
        : [],
  );

  const [activeEditorType, setActiveEditorType] =
    useState<KeyValueEditorType>("view");

  const [open, setOpen] = useState<boolean>(false);

  const handleChangeEditorType = (
    newType: KeyValueEditorType,
    isOpen: boolean,
  ) => {
    setActiveEditorType(newType);
    setOpen(isOpen);
  };

  const handleChangeSelectedKey = async (newValue: number) => {
    setCurrentSelectedKey((prev) => {
      let updated: RecordId[];
      if (mode === "assignee") {
        if (prev.includes(newValue)) {
          updated = prev.filter((id) => id !== newValue);
        } else {
          updated = [...prev, newValue];
        }
        onValueChange(updated as any);
      } else {
        updated = [newValue];
        onValueChange(newValue);

        setOpen(false);
      }
      return updated;
    });
  };

  useEffect(() => {
    if (selectedKey === undefined || selectedKey === null) return;

    const newKeys = Array.isArray(selectedKey) ? selectedKey : [selectedKey];

    setCurrentSelectedKey((prev) => {
      const isSame =
        prev.length === newKeys.length &&
        prev.every((v, i) => v === newKeys[i]);

      return isSame ? prev : newKeys;
    });
  }, [selectedKey]);

  return (
    <Popover.Root
      open={open}
      onOpenChange={(_open) => handleChangeEditorType("view", _open)}
    >
      <Popover.Trigger
        className="w-full h-full flex items-center justify-center cursor-pointer text-sm text-gray-600 hover:bg-gray-200 rounded"
        onClick={handleTriggerClick}
      >
        {triggerRenderer(currentSelectedKey[0] ?? 0, open)}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner
          side={positionerSide ?? "bottom"}
          align={positionerAlign ?? "start"}
          sideOffset={5}
        >
          <Popover.Popup
            className={`rounded bg-white border border-gray-200 shadow-xl focus:outline-none ${popupClassNames ?? "min-w-62.5 max-w-100 w-full"}`}
          >
            <Popover.Viewport>
              {activeEditorType === "view" && (
                <KeyValueMenuView
                  items={items}
                  align={align}
                  mode={mode}
                  customActionTriggerRenderer={customActionTriggerRenderer}
                  onValueChange={handleChangeSelectedKey}
                  selectedKeys={currentSelectedKey}
                  itemsEditable={itemsEditable}
                  itemsDeletable={itemsDeletable}
                  editText={editText}
                  onEditClick={() => handleChangeEditorType("edit", true)}
                  onDeleteClick={onDeleteClick}
                  onCustomActionClick={() =>
                    handleChangeEditorType("customAction", true)
                  }
                  maxHeight={maxHeight}
                />
              )}

              {itemsEditable &&
                items &&
                items.length > 0 &&
                activeEditorType === "edit" && (
                  <KeyValueMenuEdit
                    items={items}
                    align={align}
                    enumType={enumType}
                    itemsDeletable={itemsDeletable}
                    onClose={() => handleChangeEditorType("view", true)}
                  />
                )}

              {activeEditorType === "customAction" &&
                customActionTemplateRenderer &&
                customActionTemplateRenderer(() =>
                  handleChangeEditorType("view", true),
                )}
            </Popover.Viewport>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
